import { nullish } from "helpers/nullish";
import { GridApi, ValueGetterParams } from "ag-grid-community";
import { get } from "lodash";
import { AVERAGE_UNITS } from "./formulas/formula-average-units";
import { UNITS } from "./formulas/formula-units";
import { TOTAL_COST } from "./formulas/formula-total-cost";
import { TOTAL_RETAIL } from "./formulas/formula-total-retail";
import { IMU_PERCENTAGE } from "./formulas/formula-imu-percentage";
import { AUR } from "./formulas/formula-aur";
import { STORE_WITH_UNITS } from "./formulas/formula-store-with-units";
import { PLAN_ROWS } from "./formulas/formula-plan-rows";
import { UNIT_DOOR_PERCENTAGE_PLAN } from "./formulas/formula-unit-door-percentage-plan";
import { DOOR_PERCENTAGE_PLAN } from "./formulas/formula-door-percentage-plan";
import { UNIT_ROW_PERCENTAGE_PLAN } from "./formulas/formula-unit-row-percentage-plan";
import { ROW_PERCENTAGE_PLAN } from "./formulas/formula-row-percentage-plan";
import { BREADTH_PERCENTAGE_PLAN } from "./formulas/formula-breadth-percentage-plan";
import {
    buildDoorGroupNameSelected,
    DOOR_DIMENSION_METRICS_GROUP_NAME,
    DOOR_METRICS_GROUP_NAME_SEPARATOR,
} from "helpers/server-rollup";
import {
    ColumnTypes,
    MetricsTotalType,
} from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { IClasificationHierarchy, IDoor } from "helpers/door-collection";
import { generateMetricColId, getChildrenValues } from "./column-formula/ag-grid-helper";
import { Metric, MetricsKind } from "./types";

import { computeColumnFormula } from "./column-formula";
import { getTotalsRowValue } from "./totals-row";
import {
    DEFAULT_VALUES_PINNED_ROW,
    SIZE_TOTALS_IDENTIFIER,
} from "components/product-collection/product-collection-views/product-collection-grid-view/detail-list/helpers";
import {
    CLIENT_DOOR_METRICS,
    srClientDoorMetricsHash,
} from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/tabs/metrics/metrics-helpers";
import { getCDPTotalsRowValue } from "./cdp-totals-row";
import { AVERAGE_DEPTH } from "./formulas/formula-average-depth";
import { AVERAGE_COST } from "./formulas/formula-average-cost";
import { getPlanRowValue } from "./plan-rows";
import {
    ASSORTMENT_TOTALS_ROW,
    PlanMetricRows,
} from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";

const computeMetricRowGroupValue = (factoryOptions: IMetricsGetterFactoryOptions, getterParams: ValueGetterParams) => {
    const { node, column, api } = getterParams;

    return computeColumnFormula(getChildrenValues(api, node, column), factoryOptions, getterParams);
};

export const metricsFormulas = {
    UNITS,
    AVERAGE_UNITS,
    STORE_WITH_UNITS,
    TOTAL_COST,
    TOTAL_RETAIL,
    PLAN_ROWS,
    IMU_PERCENTAGE,
    AUR,
    UNIT_ROW_PERCENTAGE_PLAN,
    ROW_PERCENTAGE_PLAN,
    UNIT_DOOR_PERCENTAGE_PLAN,
    DOOR_PERCENTAGE_PLAN,
    BREADTH_PERCENTAGE_PLAN,
    AVERAGE_DEPTH,
    AVERAGE_COST,
};

export interface IMetricsGetterFactoryOptions {
    metric: Metric;
    deliveryId?: string;
    doors?: IDoor[];
    delimiter?: string;

    // SR Options to build fields
    doorId?: string;
    doorGroup?: string;
    classification?: string;
    totalType?: MetricsTotalType;
    classificationHierarchy?: IClasificationHierarchy[];

    // Filtered dimensions (All dimension values that should be included in calculation)
    filteredDeliveriesForAssortmentSet?: Set<string>;
    filteredDoorsForAssortmentSet?: Map<string, IDoor>;

    // CDP
    useSize?: boolean;
    useAllSizes?: boolean;
    disableSiblingOptimization?: boolean;

    rootGridApi?: GridApi;
}

const roundNumber = (value: number, kind: MetricsKind) => {
    if (value === 0) {
        if (kind === MetricsKind.PLAN_ROWS) {
            return 0;
        } else if (kind === MetricsKind.STORE_WITH_UNITS) {
            return undefined;
        }
    }
    if (!nullish(value)) {
        if (kind === MetricsKind.AVERAGE_UNITS) {
            return Number(value).toFixed(1);
        } else {
            return value;
        }
    }
    return undefined;
};

// Return the door metrics value
export const getServerRollupDoorMetricsValue = (
    factoryOptions: IMetricsGetterFactoryOptions,
    getterParams: ValueGetterParams
) => {
    const { colDef, data, context } = getterParams;
    const { deliveryId, doorId, doorGroup, classification, totalType, metric, classificationHierarchy } =
        factoryOptions;

    const isClientMetric = CLIENT_DOOR_METRICS.includes(metric.kind);
    const kind = isClientMetric ? srClientDoorMetricsHash[metric.kind] : metric.kind;
    const doorTotalRow = context.doorTotalRowFiltered;
    let value = null;
    let total = null;
    // Door bulk value totals
    if (!nullish(doorId)) {
        const key = buildDoorGroupNameSelected(DOOR_DIMENSION_METRICS_GROUP_NAME, doorId);
        const totalKey = (classificationHierarchy || [])
            .map((ch) => buildDoorGroupNameSelected(ch.field, ch.value))
            .join(DOOR_METRICS_GROUP_NAME_SEPARATOR);
        if (totalType === MetricsTotalType.TotalMetric) {
            value = roundNumber(
                deliveryId ? data?.doorMetrics?.[kind]?.[deliveryId]?.[key] : data?.doorMetrics?.[kind]?.[key],
                kind
            );
            total =
                isClientMetric &&
                roundNumber(
                    totalKey
                        ? deliveryId
                            ? doorTotalRow?.doorMetrics?.[kind]?.[deliveryId]?.[totalKey]
                            : doorTotalRow?.doorMetrics?.[kind]?.[""]?.[totalKey]
                        : deliveryId
                          ? doorTotalRow?.totalMetrics?.[kind]?.[deliveryId]
                          : doorTotalRow?.totalMetrics?.[kind],
                    kind
                );

            return isClientMetric ? (value && total ? (value / total) * 100 : undefined) : value;
        }

        // Door values in Grand Total section is stored in an empty property
        value = roundNumber(data?.doorMetrics?.[kind]?.[""]?.[key], kind);
        total =
            isClientMetric &&
            roundNumber(
                totalKey
                    ? doorTotalRow?.doorMetrics?.[kind]?.[""]?.[totalKey]
                    : doorTotalRow?.totalMetrics?.[kind]?.[""],
                kind
            );

        return isClientMetric ? (value && total ? (value / total) * 100 : undefined) : value;
    }

    // Metrics Totals in Grand total section
    if (
        totalType === MetricsTotalType.GrandTotalMetric &&
        colDef.colId === generateMetricColId(ColumnTypes.AllDimensionTotal, metric.kind)
    ) {
        value = roundNumber(data?.totalMetrics?.[kind]?.[""], kind);
        total = isClientMetric && roundNumber(doorTotalRow?.totalMetrics?.[kind]?.[""], kind);

        return isClientMetric && value && total ? (value / total) * 100 : value;
    }

    // Delivery Totals
    if (totalType === MetricsTotalType.TotalMetric && nullish(classification)) {
        // Grant total section does not have subtotals
        value = roundNumber(deliveryId ? data?.totalMetrics?.[kind]?.[deliveryId] : data?.doorMetrics?.[kind], kind);
        total =
            isClientMetric &&
            roundNumber(
                deliveryId ? doorTotalRow?.totalMetrics?.[kind]?.[deliveryId] : doorTotalRow?.doorMetrics?.[kind],
                kind
            );

        return isClientMetric ? (value && total ? (value / total) * 100 : undefined) : value;
    }

    // Door Group Clasification Totals
    if (!nullish(classification) && doorGroup) {
        const key = (classificationHierarchy || [])
            .map((ch) => buildDoorGroupNameSelected(ch.field, ch.value))
            .concat([buildDoorGroupNameSelected(doorGroup, classification)])
            .join(DOOR_METRICS_GROUP_NAME_SEPARATOR);

        if (totalType === MetricsTotalType.TotalMetric) {
            value = roundNumber(
                deliveryId ? data?.doorMetrics?.[kind]?.[deliveryId]?.[key] : data?.doorMetrics?.[kind]?.[key],
                kind
            );
            total =
                isClientMetric &&
                roundNumber(
                    deliveryId
                        ? doorTotalRow?.doorMetrics?.[kind]?.[deliveryId]?.[key]
                        : doorTotalRow?.doorMetrics?.[kind]?.[key],
                    kind
                );

            return isClientMetric ? (value && total ? (value / total) * 100 : undefined) : value;
        }

        // Door Group Clasification in Grand Total section is stored in an empty property
        value = roundNumber(data?.doorMetrics?.[kind]?.[""]?.[key], kind);
        total = isClientMetric && roundNumber(doorTotalRow?.doorMetrics?.[kind]?.[""]?.[key], kind);

        return isClientMetric ? (value && total ? (value / total) * 100 : undefined) : value;
    }

    // this should never happened
    return undefined;
};

export interface IMetricsGetterOptions {
    factoryOptions: IMetricsGetterFactoryOptions;
    getterParams: ValueGetterParams;
}

/**
 * Value Getter Factory function
 * @param factoryOptions any additional data not available in getter params
 * @returns a final calculated to display in metrics cell
 */
export const metricsValueGetterCreator =
    (factoryOptions: IMetricsGetterFactoryOptions) => (getterParams: ValueGetterParams) => {
        // Check if this is for a default row we should return nothing
        if (get(getterParams, "node.data.type") === DEFAULT_VALUES_PINNED_ROW) {
            return null;
        }

        if (getterParams.context?.isServerRollup && !getterParams.context?.isDetailView) {
            return getServerRollupDoorMetricsValue(factoryOptions, getterParams);
        }

        if (getterParams.node.group) {
            const groupedValue = computeMetricRowGroupValue(factoryOptions, getterParams);
            return groupedValue;
        }
        // `metric.kind` is used to select which formula to use
        const { kind } = factoryOptions.metric;
        // Totals Row Calculations and Plan Rows

        // Totals Row CDP Calculations
        if (
            getterParams.node.rowPinned &&
            get(getterParams, "node.data.type") === SIZE_TOTALS_IDENTIFIER &&
            kind === MetricsKind.UNITS
        ) {
            return getCDPTotalsRowValue({ factoryOptions, getterParams });
        }

        // Totals Row Calculations
        if (getterParams.node.rowPinned) {
            factoryOptions.disableSiblingOptimization = true;
            const rawTotals = getTotalsRowValue({ factoryOptions, getterParams });
            if (get(getterParams, "node.data.type") === SIZE_TOTALS_IDENTIFIER) {
                return rawTotals;
            }

            if (get(getterParams, "node.data.totalType") === ASSORTMENT_TOTALS_ROW) {
                return rawTotals;
            }

            const plan = getPlanRowValue({ factoryOptions, getterParams });

            if (getterParams.node.data.planType === PlanMetricRows.Plan) {
                return plan;
            } else if (getterParams.node.data.planType === PlanMetricRows.Total) {
                return rawTotals || 0;
            } else if (getterParams.node.data.planType === PlanMetricRows.Diff) {
                return plan - (rawTotals || 0);
            }
        }

        const formula = metricsFormulas[kind];
        return formula({ factoryOptions, getterParams });
    };
